import { motion, useReducedMotion } from "framer-motion";
import { CURVE_STAGES } from "../content";
import { Reveal } from "../motion";
import { Section, Wrap, SectionHead } from "./Section";

export default function EmotionalCurve() {
  const reduced = useReducedMotion();

  return (
    <Section id="experience" theme="cream" className="py-16 sm:py-24 overflow-hidden">
      <Wrap>
        <SectionHead
          eyebrow="The emotional experience curve"
          title={<>Every interaction moves the visitor from uncertainty toward agency.</>}
          intro="The future website is choreographed around six emotional moments. Scroll, and watch confidence rise."
        />

        <div className="relative mt-12 sm:mt-14">
          <svg
            className="hidden md:block absolute inset-x-0 top-0 w-full h-[300px]"
            viewBox="0 0 1000 300"
            fill="none"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <motion.path
              d="M50,250 C140,248 210,240 285,222 C370,201 430,186 500,166 C580,143 640,124 715,99 C795,72 880,52 950,38"
              stroke="#6F806E"
              strokeWidth="1.5"
              strokeDasharray="3 6"
              initial={{ pathLength: reduced ? 1 : 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: reduced ? 0 : 2.6, ease: "easeInOut" }}
            />
            <motion.path
              d="M50,250 C140,248 210,240 285,222 C370,201 430,186 500,166 C580,143 640,124 715,99 C795,72 880,52 950,38"
              stroke="#B66E50"
              strokeWidth="2"
              initial={{ pathLength: reduced ? 1 : 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: reduced ? 0 : 2.6, ease: "easeInOut", delay: 0.15 }}
              style={{ opacity: 0.9 }}
            />
          </svg>

          <div className="absolute inset-x-0 top-0 h-[300px] hidden md:block pointer-events-none" aria-hidden="true">
            {[
              { left: "8.3%", top: "82%" },
              { left: "25%", top: "76%" },
              { left: "41.7%", top: "62%" },
              { left: "58.3%", top: "49%" },
              { left: "75%", top: "33%" },
              { left: "91.7%", top: "14%" },
            ].map((pos, i) => (
              <motion.span
                key={i}
                className="absolute w-3 h-3 rounded-full bg-[#F6F0E6] border-2 border-[#B66E50] -translate-x-1/2 -translate-y-1/2"
                style={pos}
                initial={{ scale: reduced ? 1 : 0, opacity: reduced ? 1 : 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.4, delay: reduced ? 0 : 0.25 + i * 0.38 }}
              />
            ))}
          </div>

          <ol className="relative grid md:grid-cols-6 gap-10 md:gap-4" data-testid="curve-stages">
            {CURVE_STAGES.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.8} className="md:pt-[320px]">
                <li className="relative md:text-left" data-testid={`curve-stage-${s.name.toLowerCase()}`}>
                  <span className="text-[10px] font-bold tracking-[0.28em] text-[#6F806E]">{s.step}</span>
                  <h3 className="font-serif text-2xl font-medium text-[#26342E] mt-2">{s.name}</h3>
                  <p className="font-serif italic text-lg text-[#B66E50] mt-1.5 leading-snug">“{s.quote}”</p>
                </li>
              </Reveal>
            ))}
          </ol>

          <Reveal delay={2} className="mt-16 flex flex-wrap items-center gap-x-10 gap-y-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#26342E]/50">
            <span className="flex items-center gap-3"><span className="w-8 h-px bg-[#26342E]/30" />Emotional load falls</span>
            <span className="flex items-center gap-3"><span className="w-8 h-[2px] bg-[#B66E50]" />Agency and confidence rise</span>
          </Reveal>
        </div>
      </Wrap>
    </Section>
  );
}
